import * as React from "react";
import {Sparkles, Loader, ArrowRight, Stars} from "lucide-react";
import {cn} from "@/lib/utils";

export interface AIButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'primary-neon' | 'primary-cosmic' | 'primary-fire' | 'secondary' | 'success' | 'warning' | 'info' | 'gradient' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  icon?: React.ReactNode;
  showSparkles?: boolean;
  showArrow?: boolean;
  glowIntensity?: 'low' | 'medium' | 'high';
  disabled?: boolean;
  fullWidth?: boolean; // Add this prop to control width behavior
  children: React.ReactNode;
}

const variantConfig = {
  primary: {
    gradient: 'from-purple-600 via-pink-500 to-blue-500',
    glow: 'from-purple-600 via-pink-600 to-blue-600',
    textGradient: 'from-white to-pink-100',
    sparkleColors: ['text-yellow-300', 'text-pink-300'],
    shadowColor: 'purple'
  },
  'primary-neon': {
    gradient: 'from-violet-600 via-fuchsia-500 to-cyan-400',
    glow: 'from-violet-600 via-fuchsia-600 to-cyan-600',
    textGradient: 'from-white to-cyan-100',
    sparkleColors: ['text-cyan-300', 'text-fuchsia-300'],
    shadowColor: 'violet'
  },
  'primary-cosmic': {
    gradient: 'from-indigo-600 via-purple-600 to-pink-500',
    glow: 'from-indigo-600 via-purple-600 to-pink-600',
    textGradient: 'from-white to-purple-100',
    sparkleColors: ['text-purple-200', 'text-pink-200'],
    shadowColor: 'indigo'
  },
  'primary-fire': {
    gradient: 'from-rose-600 via-orange-500 to-yellow-400',
    glow: 'from-rose-600 via-orange-600 to-yellow-600',
    textGradient: 'from-white to-orange-100',
    sparkleColors: ['text-yellow-300', 'text-rose-300'],
    shadowColor: 'rose'
  },
  secondary: {
    gradient: 'from-blue-600 via-cyan-500 to-teal-500',
    glow: 'from-blue-600 via-cyan-600 to-teal-600',
    textGradient: 'from-white to-cyan-100',
    sparkleColors: ['text-cyan-200', 'text-blue-200'],
    shadowColor: 'blue'
  },
  success: {
    gradient: 'from-green-600 via-emerald-500 to-teal-500',
    glow: 'from-green-600 via-emerald-600 to-teal-600',
    textGradient: 'from-white to-green-100',
    sparkleColors: ['text-green-200', 'text-emerald-200'],
    shadowColor: 'green'
  },
  warning: {
    gradient: 'from-yellow-500 via-orange-500 to-red-500',
    glow: 'from-yellow-600 via-orange-600 to-red-600',
    textGradient: 'from-white to-yellow-100',
    sparkleColors: ['text-yellow-200', 'text-orange-200'],
    shadowColor: 'orange'
  },
  info: {
    gradient: 'from-indigo-600 via-blue-500 to-cyan-500',
    glow: 'from-indigo-600 via-blue-600 to-cyan-600',
    textGradient: 'from-white to-blue-100',
    sparkleColors: ['text-indigo-200', 'text-blue-200'],
    shadowColor: 'indigo'
  },
  gradient: {
    gradient: 'from-indigo-600 via-purple-600 to-pink-600',
    glow: 'from-indigo-600 via-purple-600 to-pink-600',
    textGradient: 'from-white to-purple-100',
    sparkleColors: ['text-white/60', 'text-white/60'],
    shadowColor: 'purple'
  },
  outline: {
    gradient: 'from-transparent to-transparent border border-gray-300',
    glow: 'from-gray-300 to-gray-300',
    textGradient: 'from-gray-700 to-gray-800',
    sparkleColors: ['text-gray-400', 'text-gray-500'],
    shadowColor: 'gray'
  }
};

const sizeConfig = {
  sm: {
    padding: 'px-4 py-2',
    text: 'text-sm',
    iconSize: 'w-3 h-3',
    iconContainer: 'p-1',
    borderRadius: 'rounded-lg',
    sparkleSize: ['w-2 h-2', 'w-1.5 h-1.5'],
    width: 'w-auto min-w-[120px]' // Add width control
  },
  md: {
    padding: 'px-6 py-3',
    text: 'text-base',
    iconSize: 'w-4 h-4',
    iconContainer: 'p-1.5',
    borderRadius: 'rounded-xl',
    sparkleSize: ['w-2.5 h-2.5', 'w-2 h-2'],
    width: 'w-auto min-w-[160px]' // Add width control
  },
  lg: {
    padding: 'px-8 py-4',
    text: 'text-lg',
    iconSize: 'w-5 h-5',
    iconContainer: 'p-2',
    borderRadius: 'rounded-2xl',
    sparkleSize: ['w-3 h-3', 'w-2.5 h-2.5'],
    width: 'w-auto min-w-[200px]' // Add width control
  },
  xl: {
    padding: 'px-8 py-4 h-16',
    text: 'text-lg font-bold',
    iconSize: 'w-5 h-5',
    iconContainer: 'p-2',
    borderRadius: 'rounded-2xl',
    sparkleSize: ['w-3 h-3', 'w-2.5 h-2.5'],
    width: 'w-auto min-w-[240px]' // Add width control
  }
};

const glowConfig = {
  low: 'opacity-20 group-hover:opacity-25',
  medium: 'opacity-25 group-hover:opacity-35',
  high: 'opacity-30 group-hover:opacity-45'
};

const AIButton = React.forwardRef<HTMLButtonElement, AIButtonProps>(
    ({
       variant = 'primary',
       size = 'md',
       loading = false,
       icon,
       showSparkles = true,
       showArrow = true,
       glowIntensity = 'medium',
       disabled = false,
       fullWidth = false, // Default to false
       className,
       children,
       ...props
     }, ref) => {

      const variantStyles = variantConfig[variant];
      const sizeStyles = sizeConfig[size];
      const glowStyles = glowConfig[glowIntensity];
      const isDisabled = disabled || loading;

      return (
          <div className={cn(
              "relative group",
              fullWidth ? "w-full" : sizeStyles.width // Conditional width
          )}>
            {/* Glowing background effect */}
            <div
                className={cn(
                    "absolute -inset-1 bg-gradient-to-r blur transition duration-300",
                    variantStyles.glow,
                    sizeStyles.borderRadius,
                    glowStyles,
                    isDisabled && "opacity-30"
                )}
            />

            {/* Main button */}
            <button
                ref={ref}
                disabled={isDisabled}
                className={cn(
                    "relative bg-gradient-to-r text-white font-bold shadow-xl transform transition-all duration-300 flex items-center gap-3 group border-0 overflow-hidden",
                    variantStyles.gradient,
                    sizeStyles.padding,
                    sizeStyles.text,
                    sizeStyles.borderRadius,
                    fullWidth ? "w-full" : "w-full", // Button takes full width of its container
                    !isDisabled && "hover:scale-105",
                    !isDisabled && `hover:shadow-${variantStyles.shadowColor}-500/25`,
                    isDisabled ? "opacity-60 cursor-not-allowed" : "hover:scale-105",
                    variant === 'outline' && "!text-gray-700 bg-white hover:bg-gray-50",
                    className
                )}
                {...props}
            >
              {/* Animated sparkles */}
              {showSparkles && !isDisabled && (
                  <>
                    <div className="absolute -top-1 -left-1">
                      <Sparkles
                          className={cn(variantStyles.sparkleColors[0], sizeStyles.sparkleSize[0], "animate-pulse")}/>
                    </div>
                    <div className="absolute -bottom-1 -right-1">
                      {variant === 'gradient' ? (
                          <Stars
                              className={cn(variantStyles.sparkleColors[1], sizeStyles.sparkleSize[1], "animate-pulse delay-500")}/>
                      ) : (
                          <Sparkles
                              className={cn(variantStyles.sparkleColors[1], sizeStyles.sparkleSize[1], "animate-pulse delay-500")}/>
                      )}
                    </div>
                  </>
              )}

              {/* Button content */}
              <div className="flex items-center justify-center gap-3 w-full">
                {/* Icon container */}
                {icon && !loading && (
                    <div className={cn(
                        "bg-white/20 rounded-full backdrop-blur-sm",
                        sizeStyles.iconContainer,
                        variant === 'outline' && "bg-gray-100"
                    )}>
                      {React.cloneElement(icon as React.ReactElement, {
                        className: cn(
                            sizeStyles.iconSize,
                            variant === 'outline' ? "text-gray-600" : "text-white",
                            variant === 'gradient' && "animate-pulse"
                        )
                      })}
                    </div>
                )}

                {/* Text with gradient */}
                <span className={cn(
                    "bg-gradient-to-r bg-clip-text text-transparent font-bold relative z-10",
                    variantStyles.textGradient,
                    variant === 'outline' && "!text-gray-700 !bg-none"
                )}>
                  {children}
                </span>

                {/* Loading spinner or arrow */}
                {loading ? (
                    <Loader className={cn(
                        sizeStyles.iconSize,
                        variant === 'outline' ? "text-gray-600" : "text-white",
                        "animate-spin"
                    )}/>
                ) : showArrow && !isDisabled ? (
                    <ArrowRight className={cn(
                        sizeStyles.iconSize,
                        variant === 'outline' ? "text-gray-600" : "text-white",
                        "group-hover:translate-x-1 transition-transform duration-300"
                    )}/>
                ) : showArrow ? (
                    <ArrowRight className={cn(
                        sizeStyles.iconSize,
                        variant === 'outline' ? "text-gray-600" : "text-white"
                    )}/>
                ) : null}
              </div>

              {/* Shine effect */}
              {!isDisabled && (
                  <div className={cn(
                      "absolute inset-0 opacity-0 group-hover:opacity-20 bg-gradient-to-r from-transparent via-white to-transparent transform skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000",
                      sizeStyles.borderRadius,
                      variant === 'gradient' && "group-hover:opacity-30 via-white/30"
                  )} />
              )}
            </button>
          </div>
      );
    }
);

AIButton.displayName = "AIButton";

export default AIButton;